/**
 * Mapbox Geocoding Service
 * Handles reverse geocoding to convert coordinates to addresses
 */

const MAPBOX_API_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

/**
 * Map of country names to ISO 3166-1 alpha-2 codes
 * Backend requires 2-character country codes
 */
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'austria': 'AT',
  'österreich': 'AT',
  'germany': 'DE',
  'deutschland': 'DE',
  'switzerland': 'CH',
  'schweiz': 'CH',
  'suisse': 'CH',
  'svizzera': 'CH',
  'italy': 'IT',
  'italien': 'IT',
  'italia': 'IT',
  'france': 'FR',
  'frankreich': 'FR',
  'spain': 'ES',
  'spanien': 'ES',
  'españa': 'ES',
  'portugal': 'PT',
  'netherlands': 'NL',
  'niederlande': 'NL',
  'belgium': 'BE',
  'belgien': 'BE',
  'czech republic': 'CZ',
  'czechia': 'CZ',
  'tschechien': 'CZ',
  'poland': 'PL',
  'polen': 'PL',
  'hungary': 'HU',
  'ungarn': 'HU',
  'slovakia': 'SK',
  'slowakei': 'SK',
  'slovenia': 'SI',
  'slowenien': 'SI',
  'croatia': 'HR',
  'kroatien': 'HR',
  'united kingdom': 'GB',
  'uk': 'GB',
  'großbritannien': 'GB',
  'united states': 'US',
  'usa': 'US',
  'vereinigte staaten': 'US',
};

/**
 * Convert country name to ISO 3166-1 alpha-2 code
 * Returns the original value if already a 2-char code or not found in mapping
 */
export function toCountryCode(country: string): string {
  if (!country) return '';
  
  // Already a 2-char code
  if (country.length === 2) return country.toUpperCase();
  
  // Look up in mapping (case-insensitive)
  const code = COUNTRY_NAME_TO_CODE[country.toLowerCase()];
  if (code) return code;
  
  // Fallback: return original (backend will validate)
  console.warn(`[toCountryCode] Unknown country name: "${country}", returning as-is`);
  return country;
}

export interface GeocodingResult {
  place_name: string;
  address?: string;
  locality?: string;
  region?: string;
  postcode?: string;
  country?: string;
  coordinates: {
    lng: number;
    lat: number;
  };
}

/**
 * Reverse geocode coordinates to get address information
 * @param lng Longitude
 * @param lat Latitude
 * @returns Address information or null if failed
 */
export async function reverseGeocode(
  lng: number,
  lat: number
): Promise<GeocodingResult | null> {
  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN;
  
  if (!accessToken) {
    console.warn('Mapbox access token not configured');
    return null;
  }

  try {
    const url = `${MAPBOX_API_URL}/${lng},${lat}.json?access_token=${accessToken}&types=address,poi&language=de`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      
      // Extract address components from Mapbox context
      // Mapbox context hierarchy: address < neighborhood < locality < place < district < region < country
      // - locality: neighborhood/district (e.g., "Favoriten")
      // - place: city (e.g., "Wien")
      // - region: state/province (e.g., "Wien" for Vienna as a state)
      const context = feature.context || [];
      const getContext = (type: string) => {
        const item = context.find((c: any) => c.id.startsWith(type));
        return item?.text || '';
      };
      
      // For locality (city), prefer 'place' over 'locality' since locality is often a district
      const place = getContext('place');
      const locality = getContext('locality');
      const region = getContext('region');
      
      return {
        place_name: feature.place_name,
        address: feature.properties?.address || feature.text,
        // City: use 'place' (city), fallback to 'region' if place is empty (for city-states like Vienna)
        locality: place || region || locality,
        // Region/State: use 'region', but if region equals place (city-state), leave empty to avoid duplication
        region: region && region !== place ? region : '',
        postcode: getContext('postcode'),
        country: getContext('country'),
        coordinates: {
          lng: feature.center[0],
          lat: feature.center[1]
        }
      };
    }
    
    // If no results, return basic coordinates
    return {
      place_name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      coordinates: { lng, lat }
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

/**
 * Format geocoding result for display
 */
export function formatAddress(result: GeocodingResult): string {
  if (result.address && result.postcode && result.locality) {
    return `${result.address}, ${result.postcode} ${result.locality}`;
  }
  return result.place_name;
}

/**
 * Extract venue name from geocoding result
 */
export function extractVenueName(result: GeocodingResult): string {
  // Try to get POI name or use locality
  const parts = result.place_name.split(',');
  if (parts.length > 0 && !parts[0].match(/^\d/)) {
    // If first part doesn't start with number (not an address), use it as venue
    return parts[0].trim();
  }
  return result.locality || 'Event Location';
}

/**
 * Full location data structure with all address components
 */
export interface FullLocationData {
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  addressComponents: {
    street: string;
    zip: string;
    locality: string;
    country: string;
    region: string;
  };
}

/**
 * Reverse geocode coordinates and return a fully populated location object
 * ready to be used in the form or API payload
 */
export async function reverseGeocodeLocation(
  coords: { lat: number; lng: number }
): Promise<FullLocationData> {
  const result = await reverseGeocode(coords.lng, coords.lat);
  
  if (result) {
    return {
      name: extractVenueName(result),
      address: formatAddress(result),
      coordinates: {
        lat: result.coordinates.lat,
        lng: result.coordinates.lng,
      },
      addressComponents: {
        street: result.address || result.place_name.split(',')[0] || '',
        zip: result.postcode || '',
        locality: result.locality || '',
        country: toCountryCode(result.country || ''),
        region: result.region || '',
      },
    };
  }
  
  // Fallback if geocoding fails
  return {
    name: 'Event Location',
    address: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
    coordinates: {
      lat: coords.lat,
      lng: coords.lng,
    },
    addressComponents: {
      street: '',
      zip: '',
      locality: '',
      country: '',
      region: '',
    },
  };
}

/**
 * Normalize location data by geocoding coordinates or searching address
 * This ensures consistent address format (e.g., country as "Austria" not "AT" or "Österreich")
 * 
 * Strategy:
 * 1. If coordinates exist → reverse geocode to get normalized address
 * 2. If no coordinates but address exists → forward geocode to get coordinates and normalized address
 * 3. If neither → return original data unchanged
 */
export async function normalizeLocation(
  location: {
    name?: string;
    address?: string;
    coordinates?: { lat: number; lng: number };
    addressComponents?: {
      street?: string;
      zip?: string;
      locality?: string;
      country?: string;
      region?: string;
    };
  }
): Promise<FullLocationData | null> {
  // Case 1: Has valid coordinates → reverse geocode
  if (location.coordinates && location.coordinates.lat !== 0 && location.coordinates.lng !== 0) {
    console.log('[normalizeLocation] Normalizing via reverse geocode:', location.coordinates);
    const normalized = await reverseGeocodeLocation(location.coordinates);
    
    // Preserve original name if it exists and is meaningful
    if (location.name && location.name.trim() && location.name !== 'Event Location') {
      normalized.name = location.name;
    }
    
    return normalized;
  }
  
  // Case 2: No coordinates but has address → forward geocode
  const addressString = buildAddressString(location);
  if (addressString) {
    console.log('[normalizeLocation] Normalizing via forward geocode:', addressString);
    const results = await searchAddress(addressString, { limit: 1 });
    
    if (results.length > 0) {
      const feature = results[0];
      const context = feature.context || [];
      const getContext = (type: string) => {
        const item = context.find((c: { id: string; text: string }) => c.id.startsWith(type));
        return item?.text || '';
      };
      
      const place = getContext('place');
      const locality = getContext('locality');
      const region = getContext('region');
      
      return {
        name: location.name || extractVenueName({
          place_name: feature.place_name,
          address: feature.properties?.address || feature.text,
          locality: place || region || locality,
          coordinates: { lng: feature.center[0], lat: feature.center[1] }
        }),
        address: feature.place_name,
        coordinates: {
          lat: feature.center[1],
          lng: feature.center[0],
        },
        addressComponents: {
          street: feature.properties?.address || feature.text || '',
          zip: getContext('postcode'),
          locality: place || region || locality,
          country: toCountryCode(getContext('country')),
          region: region && region !== place ? region : '',
        },
      };
    }
  }
  
  // Case 3: No coordinates and no address → return null (cannot normalize)
  console.log('[normalizeLocation] Cannot normalize - no coordinates or address');
  return null;
}

/**
 * Build an address string from location components for forward geocoding
 */
function buildAddressString(location: {
  address?: string;
  addressComponents?: {
    street?: string;
    zip?: string;
    locality?: string;
    country?: string;
    region?: string;
  };
}): string {
  // Try full address string first
  if (location.address && location.address.trim()) {
    return location.address.trim();
  }
  
  // Build from components
  const components = location.addressComponents;
  if (!components) return '';
  
  const parts: string[] = [];
  if (components.street) parts.push(components.street);
  if (components.zip) parts.push(components.zip);
  if (components.locality) parts.push(components.locality);
  if (components.region && components.region !== components.locality) parts.push(components.region);
  if (components.country) parts.push(components.country);
  
  return parts.join(', ');
}

/**
 * Search for addresses using Mapbox forward geocoding
 * @param query Search query
 * @param options Additional search options
 * @returns Array of matching locations
 */
export async function searchAddress(
  query: string,
  options: {
    limit?: number;
    types?: string;
    language?: string;
    country?: string;
    bbox?: [number, number, number, number];
    proximity?: { lng: number; lat: number };
  } = {}
): Promise<any[]> {
  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN;
  
  console.log('🔑 Mapbox token available:', !!accessToken, accessToken ? `(${accessToken.substring(0, 10)}...)` : 'NO TOKEN');
  
  if (!accessToken) {
    console.warn('⚠️ Mapbox access token not configured! Set VITE_MAPBOX_ACCESS_TOKEN in .env');
    return [];
  }

  try {
    // Build query parameters
    const params = new URLSearchParams({
      access_token: accessToken,
      types: options.types || 'address,poi',
      limit: String(options.limit || 5),
      language: options.language || 'de,en'
    });

    // Add optional parameters
    if (options.country) {
      params.append('country', options.country);
    }
    if (options.bbox) {
      params.append('bbox', options.bbox.join(','));
    }
    if (options.proximity) {
      params.append('proximity', `${options.proximity.lng},${options.proximity.lat}`);
    }

    const url = `${MAPBOX_API_URL}/${encodeURIComponent(query)}.json?${params}`;
    console.log('🌐 Fetching from URL:', url.replace(accessToken, 'TOKEN_HIDDEN'));
    
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error:', response.status, response.statusText, errorText);
      throw new Error(`Geocoding search failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('📦 API Response:', data);
    return data.features || [];
  } catch (error) {
    console.error('💥 Address search error:', error);
    return [];
  }
}
